import React from "react";
import { ActionIcon, Box, Skeleton, Stack, Text } from "@mantine/core";
import { IconCoins, IconPencil } from "@tabler/icons-react";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../../types/transactions";
import { TransactionTotals } from "../../../utils/transactionTotals";
import {
  calculateDiscountedItemPrice,
  hasLineDiscount,
} from "../../../utils/discountUtils";
import ServiceModifiers from "../../common/ServiceModifiers/ServiceModifiers";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import { perUnitBreakdown } from "../../../utils/quantityUtils";
import { remainingLinePricing } from "../../../utils/packageRedemptionUtils";
import { SalonService } from "../../../types/context";
import { useLoyaltyPreviewEarn } from "../../../hooks/useLoyaltyPreviewEarn";
import { LoyaltyRewardPick } from "../../rewards/LoyaltyRewardPickerModal";
import SurfacePaper from "../../common/surface/SurfacePaper";
import SoftNotePanel from "../../common/surface/SoftNotePanel";
import DashedActionRow from "../../common/surface/DashedActionRow";
import styles from "./CheckoutPanel.module.css";

interface CheckoutSummaryPanelProps {
  transaction: GetTransactionsResponse | TransactionResponse;
  totals: TransactionTotals;
  payMethod: string | null;
  /** override ?? remaining balance — what the charge button will take. */
  chargeAmount: number;
  overrideAmount: number | null;
  /** Opens the TipModal; undefined hides tip editing (read-only). */
  onEditTips?: () => void;
  /** Package-sale checkout (no line items): the sale name + amount. */
  packageSaleName?: string;
  amountDue?: number;
  amountNote?: string;
  /** Salon service catalog — used to look up price_basis so per-unit/syringe
   *  services show their "N units × $price" breakdown. */
  salonServices?: SalonService[];
  /** $-off currently staged for a Loyalty commit-at-Charge redemption.
   *  Forwarded to the earn preview, which nets it off the earn basis server
   *  side, so the earn line always shows the backend number as-is. */
  stagedLoyaltyDollars?: number;
  /** Loyalty picks staged from Appointment Details (Rewards & Promotions).
   *  Rendered as negative line items right before Total so the summary
   *  Total matches the final charged amount (once redeem commits, the
   *  balance sheet stays consistent). */
  stagedRewards?: LoyaltyRewardPick[];
}

/**
 * Right checkout paper — "Transaction Summary": tips block (terminal note
 * for Card Present, per-staff editable list otherwise), services/products
 * totals with strike-through discounts, then the summary rows in the same
 * order as the appointment-detail summary (Screen 1) — Subtotal (net of
 * package redemptions) → Discount → per-rate Tax → Tip — plus a checkout-only
 * Paid row, a dashed divider, the large Total / Balance Due, and the orange
 * override chip when a custom charge amount is set.
 */
export default function CheckoutSummaryPanel({
  transaction,
  totals,
  payMethod,
  chargeAmount,
  overrideAmount,
  onEditTips,
  packageSaleName,
  amountDue,
  amountNote,
  salonServices,
  stagedLoyaltyDollars = 0,
  stagedRewards = [],
}: CheckoutSummaryPanelProps) {
  // The terminal reader collects the tip for a Card payment — but only when
  // there's a service value for it to compute a percentage against. On a
  // zero-service ticket the reader can't, so the operator types the tip in-app
  // (the "Add tip" affordance below) instead of seeing the terminal note.
  const readerCollectsTip =
    payMethod === "Card" && totals.undiscountedServiceTotal > 0;
  const hasBookedServices =
    (transaction.line_items?.booked_services?.length ?? 0) > 0;

  // Points the transaction will earn on settlement (backend PR #499).
  // Package-sale transactions have no billable client-line items and no
  // earn preview to display; other checkout transactions show it near the
  // Total. Endpoint may not be deployed yet — hook returns null on failure
  // and the line is hidden.
  // Preview-earn now takes the staged (uncommitted) redemption dollars as
  // a query param; the backend runs the same accrual math with that amount
  // netted off the earn basis, so the returned points are truthful for
  // both no-staging and staged states — no local estimation, no "(est)"
  // suffix. Refetches automatically when stagedLoyaltyDollars changes
  // (hook's mutationKey covers it).
  const { preview: earnPreview, isLoading: earnLoading } =
    useLoyaltyPreviewEarn(
      packageSaleName ? null : transaction,
      stagedLoyaltyDollars,
    );
  const expectedPoints =
    typeof earnPreview?.expected_points === "number" &&
    earnPreview.expected_points > 0
      ? earnPreview.expected_points
      : 0;

  // Discount broken out on its own row, matching the appointment-detail summary
  // (Screen 1). On the remaining-units basis this is the full→discounted delta
  // on the units the client still pays for (redeemed units drop out), so it
  // foots against the netted Subtotal below.
  const discountSavings = totals.discountSavings;

  // Per-staff tip lines (aggregated across each staff member's services).
  const tipsByStaff = (() => {
    const byStaff = new Map<string, { name: string; tip: number }>();
    (transaction.line_items?.booked_services ?? []).forEach((service) => {
      if (!service.staff_id) return;
      const existing = byStaff.get(service.staff_id);
      if (existing) {
        existing.tip += service.tip || 0;
      } else {
        byStaff.set(service.staff_id, {
          name: service.staff_name,
          tip: service.tip || 0,
        });
      }
    });
    return Array.from(byStaff.values()).filter((entry) => entry.tip > 0);
  })();

  const renderTotalValue = (
    undiscounted: number,
    discounted: number,
    isDiscounted: boolean,
  ) =>
    isDiscounted ? (
      <Text component="span">
        <Text component="span" className={styles.strikePrice}>
          {formatCurrency(undiscounted)}
        </Text>
        <Text component="span" className={styles.discountedValue}>
          {formatCurrency(discounted)}
        </Text>
      </Text>
    ) : (
      <Text component="span" className={styles.totalsValue}>
        {formatCurrency(undiscounted)}
      </Text>
    );

  return (
    <SurfacePaper>
      <Box className={styles.summaryHeadingRow}>
        <Box className={styles.summaryHeading}>Transaction Summary</Box>
      </Box>

      {/* Package sale: a single named row, no tips/line items. */}
      {packageSaleName ? (
        <>
          <Box className={styles.totals}>
            <Box className={styles.totalsRow}>
              <Text component="span">{packageSaleName}</Text>
              <Text component="span" className={styles.totalsValue}>
                {formatCurrency(amountDue ?? chargeAmount)}
              </Text>
            </Box>
            {amountNote && (
              <Box className={styles.totalsRow}>
                <Text component="span" className={styles.amountNote}>
                  {amountNote}
                </Text>
              </Box>
            )}
          </Box>
          <Box className={styles.dashedDivider} />
          <Box className={styles.totalRow}>
            <Box component="span" className={styles.totalLabel}>
              Total
            </Box>
            <Box component="span" className={styles.totalAmount}>
              {formatCurrency(amountDue ?? chargeAmount)}
            </Box>
          </Box>
        </>
      ) : (
        <>
          {/* Tips */}
          {hasBookedServices && (
            <>
              {readerCollectsTip ? (
                <SoftNotePanel icon={<IconCoins size={15} />}>
                  Tips are collected directly on the payment terminal.
                </SoftNotePanel>
              ) : tipsByStaff.length > 0 ? (
                <Box className={styles.tipsBlock}>
                  <Box style={{ flex: 1 }}>
                    <Box className={styles.tipsLabel}>
                      <IconCoins
                        size={15}
                        color="var(--mantine-color-dimmed)"
                      />
                      Tips
                    </Box>
                    <Box className={styles.tipsList}>
                      {tipsByStaff.map((entry) => (
                        <Box key={entry.name}>
                          {formatCurrency(entry.tip)} · {entry.name}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  {onEditTips && (
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      onClick={onEditTips}
                      aria-label="Edit tips"
                    >
                      <IconPencil size={15} />
                    </ActionIcon>
                  )}
                </Box>
              ) : onEditTips ? (
                <DashedActionRow
                  icon={<IconCoins size={15} />}
                  label="No tips added"
                  actionLabel="Add tip"
                  onClick={onEditTips}
                />
              ) : null}
              <Box className={styles.hairlineDivider} />
            </>
          )}

          {/* Dimmed per-item rows (services w/ their adjustments, then
              products); the Subtotal below shows their net-of-redemption
              (pre-discount) total, with the discount broken out on its own
              row. */}
          <Box className={styles.totals}>
            {(transaction.line_items?.booked_services ?? []).map((service) => {
              const priceBasis = (salonServices ?? []).find(
                (svc) => svc.id === service.salon_service_id,
              )?.price_basis;
              // Show only the portion still billed after package redemptions:
              // remaining units at their (discounted) price. Per-unit / per-
              // syringe services show a "N units × $price" breakdown under the
              // name (same as the appointment summary).
              const { remainingUnits, full, discounted, hasDiscount } =
                remainingLinePricing(service);
              const breakdown = perUnitBreakdown(
                remainingUnits,
                priceBasis,
                service.unit_price,
              );
              return (
                <Box key={service.id}>
                  <Box className={styles.totalsRow}>
                    <Box>
                      <Text component="span">{service.name}</Text>
                      {breakdown && (
                        <Text component="div" className={styles.amountNote}>
                          {breakdown}
                        </Text>
                      )}
                    </Box>
                    {renderTotalValue(full, discounted, hasDiscount)}
                  </Box>
                  <ServiceModifiers
                    subLineItems={service.sub_line_items}
                    serviceName={service.name}
                  />
                </Box>
              );
            })}
            {(transaction.line_items?.products ?? []).map((product) => (
              <Box className={styles.totalsRow} key={product.id}>
                <Text component="span">
                  {product.name}
                  {product.quantity > 1 ? ` × ${product.quantity}` : ""}
                </Text>
                {renderTotalValue(
                  product.unit_price * product.quantity,
                  calculateDiscountedItemPrice(product, product.quantity),
                  hasLineDiscount(product),
                )}
              </Box>
            ))}
            {/* Gift cards sold as line items: a flat face value — never
                discounted or taxed — so a single name + amount row (mirrors the
                appointment-detail summary on Screen 1). Its amount is already
                folded into Subtotal/Total via computeTransactionTotals; this
                row makes the visible items foot against that Subtotal. */}
            {(transaction.line_items?.gift_cards ?? []).map((giftCard) => (
              <Box className={styles.totalsRow} key={giftCard.line_item_id}>
                <Text component="span">{giftCard.name}</Text>
                <Text component="span" className={styles.totalsValue}>
                  {formatCurrency(giftCard.amount)}
                </Text>
              </Box>
            ))}
          </Box>

          <Box className={styles.hairlineDivider} />

          <Box className={styles.totals}>
            <Box className={styles.totalsRow}>
              <Text component="span">Subtotal</Text>
              <Text component="span" className={styles.totalsValue}>
                {formatCurrency(totals.subtotalAfterRedemptions)}
              </Text>
            </Box>
            {discountSavings > 0 && (
              <Box className={styles.totalsRow}>
                <Text component="span">Discount</Text>
                <Text component="span" className={styles.discountValue}>
                  −{formatCurrency(discountSavings)}
                </Text>
              </Box>
            )}
            {totals.taxBreakdown.map((tax) => (
              <Box className={styles.totalsRow} key={tax.id}>
                <Text component="span">
                  {tax.name} ({(tax.rate * 100).toFixed(3)}%)
                </Text>
                <Text component="span" className={styles.totalsValue}>
                  {formatCurrency(tax.amount)}
                </Text>
              </Box>
            ))}
            <Box className={styles.totalsRow}>
              <Text component="span">Tip</Text>
              <Text component="span" className={styles.totalsValue}>
                {formatCurrency(totals.tipsTotal)}
              </Text>
            </Box>
            {totals.paidAmount > 0 && (
              <Box className={styles.totalsRow}>
                <Text component="span">Paid</Text>
                <Text component="span" className={styles.paidValue}>
                  −{formatCurrency(totals.paidAmount)}
                </Text>
              </Box>
            )}
            {/* Loyalty picks aggregate into ONE "Redeem N points for $X off"
                line per 2026-08-26 feedback — the same summary shape used on
                the Appointment Details rewards panel. Subtotal + tax stay
                on the full pre-reward amounts. */}
            {stagedRewards.length > 0 &&
              (() => {
                const totalPoints = stagedRewards.reduce(
                  (acc, pick) =>
                    acc +
                    pick.quantity * (Number(pick.reward.points_cost) || 0),
                  0,
                );
                const totalDollars = stagedRewards.reduce(
                  (acc, pick) =>
                    acc +
                    pick.quantity * (Number(pick.reward.applied_value) || 0),
                  0,
                );
                return (
                  <Box className={styles.totalsRow}>
                    <Text component="span">
                      Redeem {totalPoints.toLocaleString("en-US")} points for{" "}
                      {formatCurrency(totalDollars)} off
                    </Text>
                    <Text component="span" className={styles.discountValue}>
                      −{formatCurrency(totalDollars)}
                    </Text>
                  </Box>
                );
              })()}
          </Box>

          <Box className={styles.dashedDivider} />

          <Box className={styles.totalRow}>
            <Box component="span" className={styles.totalLabel}>
              {totals.paidAmount > 0 ? "Balance Due" : "Total"}
            </Box>
            <Box component="span" className={styles.totalAmount}>
              {formatCurrency(
                Math.max(
                  0,
                  totals.remainingBalance -
                    stagedRewards.reduce(
                      (acc, pick) =>
                        acc +
                        pick.quantity *
                          (Number(pick.reward.applied_value) || 0),
                      0,
                    ),
                ),
              )}
            </Box>
          </Box>
          {earnLoading ? (
            <Skeleton
              height={12}
              width={110}
              mt={6}
              ml="auto"
              data-testid="loyalty-expected-points-skeleton"
            />
          ) : (
            expectedPoints > 0 &&
            (() => {
              // preview-earn returns the FORECAST for the appointment
              // fully paid. Labeling it "points earned" reads as
              // already-banked, but accrual is all-or-nothing at
              // status='paid'. Split proportionally to paid-so-far so the
              // checkout reads "Earned M points" (paid-so-far share) +
              // "Will earn N total points" (full appointment). Single
              // "Will earn" line when nothing paid yet.
              const paidSoFar = totals.paidAmount;
              const totalForEarn = totals.totalAfterRedemptions;
              const earnedSoFar =
                paidSoFar > 0 && totalForEarn > 0
                  ? Math.floor(
                      expectedPoints * Math.min(1, paidSoFar / totalForEarn),
                    )
                  : 0;
              return (
                <Stack gap={2} mt={4}>
                  {earnedSoFar > 0 && (
                    <Text
                      size="xs"
                      c="dimmed"
                      ta="right"
                      data-testid="loyalty-earned-so-far"
                    >
                      Earned {earnedSoFar.toLocaleString("en-US")} points
                    </Text>
                  )}
                  <Text
                    size="xs"
                    c="dimmed"
                    ta="right"
                    data-testid="loyalty-expected-points"
                  >
                    Will earn {expectedPoints.toLocaleString("en-US")} total
                    points
                  </Text>
                </Stack>
              );
            })()
          )}
        </>
      )}

      {overrideAmount !== null && (
        <Box className={styles.overrideChip}>
          <Box component="span" className={styles.overrideChipLabel}>
            <IconPencil size={13} />
            Charge amount overridden
          </Box>
          <Box component="span" className={styles.overrideChipAmount}>
            {formatCurrency(chargeAmount)}
          </Box>
        </Box>
      )}
    </SurfacePaper>
  );
}
